<script setup lang="ts">
import { ref } from "vue";
import { useTranslation } from "i18next-vue";
import type { Finding } from "@/types/findings.types";
import DRBtnIcon from "@/components/UI/DRBtnIcon.vue";
import DRDropdownItem from "@/components/UI/DRDropdownItem.vue";
import ConfirmationModal from "@/components/generic/ConfirmationModal.vue";
import MoveSectionModal from "../photos/MoveSectionModal.vue";
import SourceObservationDrawer from "./SourceObservationDrawer.vue";

defineProps<{
  finding: Finding;
  reportId: number;
  isDeleting?: boolean;
}>();

const emit = defineEmits<{
  (e: "delete"): void;
  (e: "move", sectionId: number | null): void;
  (e: "edit"): void;
}>();

const { t } = useTranslation();

// Delete confirmation state
const showDeleteConfirm = ref(false);

// Move modal state
const showMoveModal = ref(false);

// Source observation drawer
const showObservationDrawer = ref(false);

const handleDelete = () => {
  emit("delete");
  showDeleteConfirm.value = false;
};

const handleMove = (sectionId: number | null) => {
  emit("move", sectionId);
  showMoveModal.value = false;
};
</script>

<template>
  <el-dropdown trigger="click">
    <DRBtnIcon
      icon="bi-three-dots"
      variant="flat"
      :size="32"
      class="action-btn"
      @click.stop
    />
    <template #dropdown>
      <el-dropdown-menu>
        <DRDropdownItem
          icon="bi-pencil"
          :label="t('report:findings.actions.edit')"
          @select="emit('edit')"
        />
        <DRDropdownItem
          icon="bi-folder-symlink"
          :label="t('report:findings.actions.move')"
          @select="showMoveModal = true"
        />
        <DRDropdownItem
          v-if="finding.sourceObservations?.length"
          icon="bi-binoculars"
          :label="t('report:findings.sourceObservation.button')"
          @select="showObservationDrawer = true"
        />

        <div class="dropdown-divider"></div>

        <DRDropdownItem
          icon="bi-trash"
          :label="t('report:findings.actions.delete')"
          type="danger"
          :loading="isDeleting"
          @select="showDeleteConfirm = true"
        />
      </el-dropdown-menu>
    </template>
  </el-dropdown>

  <!-- Delete confirmation modal -->
  <ConfirmationModal
    v-model="showDeleteConfirm"
    :title="t('report:findings.deleteModal.title')"
    :message="t('report:findings.deleteModal.message')"
    :confirm-label="t('delete')"
    :loading="isDeleting"
    danger
    @confirm="handleDelete"
  />

  <!-- Move to section modal -->
  <MoveSectionModal
    v-model="showMoveModal"
    :report-id="reportId"
    :title="t('report:findings.actions.moveTitle')"
    @select="handleMove"
  />

  <!-- Source observations drawer -->
  <SourceObservationDrawer
    v-if="finding.sourceObservations?.length"
    v-model="showObservationDrawer"
    :observations="finding.sourceObservations"
  />
</template>

<style lang="scss" scoped>
.dropdown-divider {
  height: 1px;
  margin: 4px 0;
  background-color: var(--el-border-color);
}
</style>
